import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { MasterControlPerformance } from "@/hooks/useMasterControl";
import MCStatusBadge from "./MCStatusBadge";

type MCPerformanceIndicatorsProps = {
  performance: MasterControlPerformance | null;
  loading?: boolean;
  onRefresh?: () => Promise<void>;
};

const INDICATOR_CONFIG = [
  {
    key: "cacheHitRatio",
    label: "API cache",
    format: (value: number) => `${Math.round(value * 100)}% cache hits`,
    status: (value: number) => {
      if (value >= 0.85) return "ok";
      if (value >= 0.6) return "warn";
      return "error";
    },
  },
  {
    key: "redisLatencyMs",
    label: "Redis latency",
    format: (value: number) => `${Math.round(value)} ms`,
    status: (value: number) => {
      if (value <= 80) return "ok";
      if (value <= 200) return "warn";
      return "error";
    },
  },
  {
    key: "databaseLatencyMs",
    label: "DB latency",
    format: (value: number) => `${Math.round(value)} ms`,
    status: (value: number) => {
      if (value <= 120) return "ok";
      if (value <= 300) return "warn";
      return "error";
    },
  },
  {
    key: "chromaLatencyMs",
    label: "Chroma latency",
    format: (value: number) => `${Math.round(value)} ms`,
    status: (value: number) => {
      if (value <= 150) return "ok";
      if (value <= 400) return "warn";
      return "error";
    },
  },
  {
    key: "workflowThroughput",
    label: "Workflow throughput",
    format: (value: number) => `${value.toFixed(1)} w/s`,
    status: (value: number) => {
      if (value >= 0.8) return "ok";
      if (value >= 0.4) return "warn";
      return "error";
    },
  },
  {
    key: "ingestionThroughput",
    label: "Sync throughput",
    format: (value: number) => `${value.toFixed(1)} jobs/s`,
    status: (value: number) => {
      if (value >= 0.7) return "ok";
      if (value >= 0.3) return "warn";
      return "error";
    },
  },
] as const;

export function MCPerformanceIndicators({ performance, loading, onRefresh }: MCPerformanceIndicatorsProps) {
  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">Performance indicators</CardTitle>
          <p className="text-sm text-muted-foreground">Live latencies and throughput across core services.</p>
        </div>
        {onRefresh && (
          <Button variant="outline" size="sm" disabled={loading} onClick={() => void onRefresh()}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {performance ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {INDICATOR_CONFIG.map((indicator) => {
              const value = (performance as Record<string, number | undefined>)[indicator.key] ?? 0;
              const status = indicator.status(Number(value));
              return (
                <div key={indicator.key} className="rounded-2xl border border-border/60 bg-background/50 p-4">
                  <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
                    <span>{indicator.label}</span>
                    <MCStatusBadge label={indicator.format(Number(value))} status={status} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Performance snapshot unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
}

export default MCPerformanceIndicators;
